import React, { useCallback, useEffect, useState } from "react";
import { Box, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, makeStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { AsyncSection, Badge, Button } from "../ds";
import tokens from "../designTokens";
import { MONO, useTableStyles } from "../tableStyles";
import { istToday } from "../fleetRange";
import { actionBacklog, agreementWeekly, dreamPromotions, dreamReadiness, qualityByVersion, toolFailures, unpromoteDream } from "./api";
import { fmtRate, fmtTime } from "./shared";

const useStyles = makeStyles({
  section: { background: tokens.surface, border: `1px solid ${tokens.border}`, borderRadius: 10, padding: 14 },
  head: { fontFamily: tokens.fontHeading, fontWeight: 600, fontSize: 14, color: "#232D2F" },
  sub: { fontFamily: tokens.fontBody, fontSize: 12, color: tokens.textSecondary, margin: "2px 0 10px" },
  hint: { fontFamily: tokens.fontBody, fontSize: 11.5, color: tokens.textHint },
});

const VERDICT_TONE = { improved: "success", flat: "neutral", too_early: "neutral", regressed: "error" };

function useLoad(fn, deps) {
  const [state, setState] = useState({ loading: true, notFound: false, error: null, data: null });
  const load = useCallback(() => {
    let alive = true;
    setState((s) => ({ ...s, loading: true }));
    fn().then((r) => alive && setState({ ...r, loading: false }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(load, [load]);
  return [state, load];
}

function Section({ title, subtitle, action, children }) {
  const classes = useStyles();
  return (
    <div className={classes.section}>
      <Box display="flex" alignItems="center" gridGap={8}>
        <Typography component="div" className={classes.head}>{title}</Typography>
        {action && <span style={{ marginLeft: "auto" }}>{action}</span>}
      </Box>
      {subtitle && <Typography component="div" className={classes.sub}>{subtitle}</Typography>}
      {children}
    </div>
  );
}

function Grid({ columns, rows, numeric = [] }) {
  const table = useTableStyles();
  return (
    <div className={table.tableWrap}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((h, i) => <TableCell key={h} className={`${table.headCell} ${numeric.includes(i) ? table.num : ""}`}>{h}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((cells, r) => (
            <TableRow key={r}>
              {cells.map((c, i) => <TableCell key={i} className={`${table.cell} ${numeric.includes(i) ? table.num : ""}`}>{c}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const Agent = ({ projectId, agentId }) => (
  <>
    <div>{projectId || "—"}</div>
    <div style={{ fontFamily: MONO, fontSize: 11, color: tokens.textHint }}>{agentId}</div>
  </>
);

function Readiness({ refreshKey }) {
  const [s] = useLoad(() => dreamReadiness({ days: 14 }), [refreshKey]);
  const d = s.data;
  const t = d?.thresholds || {};
  return (
    <Section
      title="Dream readiness"
      subtitle={`Ready = last ${t.nights ?? 3} nights judged and each night supported ≥ ${fmtRate(t.supported_min)}, misleading ≤ ${fmtRate(t.misleading_max)}, learned safe ≥ ${fmtRate(t.learned_safe_min)}.`}
      action={d && <Badge tone={d.ready_agents ? "success" : "neutral"}>{d.ready_agents} ready of {(d.agents || []).length}</Badge>}
    >
      <AsyncSection loading={s.loading} notFound={s.notFound} error={s.error} empty={!(d?.nights || []).length} emptyTitle="No dreams in the last 14 days">
        <Grid
          columns={["Night", "Agents", "Judged", "Unjudged", "Supported", "Misleading", "Facts dropped", "Learned safe", "Gaps knowledge", "Labels ok", "Passing agents", "Cost"]}
          numeric={[1, 2, 3, 6, 10]}
          rows={[...(d?.nights || [])].reverse().map((n) => [
            n.date, n.agents, n.judged, n.unjudged ? <Badge tone="warning" subtle>{n.unjudged}</Badge> : 0,
            fmtRate(n.supported_rate), fmtRate(n.misleading_rate), n.facts_dropped, fmtRate(n.learned_safe_rate),
            fmtRate(n.gaps_knowledge_rate), fmtRate(n.gap_labels_ok_rate), n.passing_agents, `$${Number(n.cost_usd || 0).toFixed(3)}`,
          ])}
        />
      </AsyncSection>
    </Section>
  );
}

function Promotions({ refreshKey }) {
  const [s, reload] = useLoad(() => dreamPromotions({ days: 30 }), [refreshKey]);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const items = s.data?.items || [];
  const counts = s.data?.counts || {};

  const onUnpromote = async (p) => {
    if (!window.confirm(`Remove the ${p.lines.length} line(s) promoted on ${p.date} from this agent's live Learned memory? They will not be promoted again.`)) return;
    setBusy(`${p.agent_id}:${p.date}`);
    const r = await unpromoteDream(p.project_id, p.date, p.agent_id);
    setBusy(null);
    if (r.status === 409) setMsg({ severity: "warning", text: "Live memory changed during the write; try again." });
    else if (r.error || r.notFound) setMsg({ severity: "error", text: r.error || "Nothing was promoted on that date." });
    else {
      setMsg({ severity: "success", text: `Removed ${(r.data?.removed || []).length} line(s).` });
      reload();
    }
  };

  const delta = (b, a) => `${fmtRate(b)} → ${fmtRate(a)}`;
  return (
    <Section
      title="Promotions"
      subtitle="Judged episodes in the 7 days before each promotion against the days after it. Regressed = significantly worse goal rate, or more bad claims or missed handoffs."
      action={<Box display="flex" gridGap={6}>{Object.entries(counts).map(([k, v]) => <Badge key={k} tone={VERDICT_TONE[k] || "neutral"} subtle>{k} {v}</Badge>)}</Box>}
    >
      {msg && <Alert severity={msg.severity} style={{ marginBottom: 8 }}>{msg.text}</Alert>}
      <AsyncSection loading={s.loading} notFound={s.notFound} error={s.error} empty={!items.length} emptyTitle="No promotions in the last 30 days">
        <Grid
          columns={["Agent", "Promoted", "Lines", "Verdict", "Goal rate", "Bad claims", "Missed handoff", "Episodes", ""]}
          rows={items.map((p) => [
            <Agent projectId={p.project_id} agentId={p.agent_id} />,
            fmtTime(p.promoted_at),
            <div title={(p.lines || []).join("\n")}>{(p.lines || []).length}</div>,
            <Box display="flex" gridGap={4} flexWrap="wrap">
              <Badge tone={VERDICT_TONE[p.verdict] || "neutral"} subtle>{p.verdict}</Badge>
              {p.unpromoted_at && <Badge tone="neutral" subtle>unpromoted</Badge>}
              {!p.complete && p.verdict !== "too_early" && <Badge tone="neutral" subtle>window open</Badge>}
            </Box>,
            delta(p.before?.goal_rate, p.after?.goal_rate),
            delta(p.before?.bad_claim_rate, p.after?.bad_claim_rate),
            delta(p.before?.handoff_missed_rate, p.after?.handoff_missed_rate),
            `${p.before?.episodes ?? 0} / ${p.after?.episodes ?? 0}`,
            !p.unpromoted_at && (p.lines || []).length > 0 && (
              <Button size="sm" variant={p.verdict === "regressed" ? "danger" : "outlined"} disabled={busy === `${p.agent_id}:${p.date}`} onClick={() => onUnpromote(p)}>
                Unpromote
              </Button>
            ),
          ])}
        />
      </AsyncSection>
    </Section>
  );
}

function Agreement({ refreshKey }) {
  const [s] = useLoad(() => agreementWeekly({ weeks: 8 }), [refreshKey]);
  const d = s.data;
  const alert = d?.alert;
  return (
    <Section title="Judge vs human agreement" subtitle={`Cohen's kappa per week of labelling, on judge-flagged episodes. Alert under ${d?.floor ?? 0.6}, or a drop of 0.15.`}>
      {alert && alert.reason !== "unmeasured" && (
        <Alert severity="error" style={{ marginBottom: 8 }}>
          Week of {alert.week}: goal agreement {alert.kappa} ({alert.reason === "dropped" ? `down from ${alert.earlier_mean}` : "below the floor"}).
        </Alert>
      )}
      {alert?.reason === "unmeasured" && <Alert severity="info" style={{ marginBottom: 8 }}>Not enough labels to measure: {alert.detail}. Label the review queue to keep the judge honest.</Alert>}
      <AsyncSection loading={s.loading} notFound={s.notFound} error={s.error} empty={!(d?.weeks || []).length} emptyTitle="No labels in the last 8 weeks">
        <Grid
          columns={["Week", "Labels", "Goal kappa", "Goal pairs", "Claim kappa", "Claim pairs", "Judge versions"]}
          numeric={[1, 3, 5]}
          rows={[...(d?.weeks || [])].reverse().map((w) => [
            w.week, w.labels, w.goal?.kappa ?? "—", w.goal?.n, w.claims?.kappa ?? "—", w.claims?.n, (w.judge_versions || []).join(", ") || "—",
          ])}
        />
      </AsyncSection>
    </Section>
  );
}

function Versions({ refreshKey }) {
  const [s] = useLoad(() => qualityByVersion({ days: 7 }), [refreshKey]);
  const d = s.data;
  const alert = d?.alert;
  return (
    <Section title="Quality by deployed version" subtitle={`Judged episodes per gen-ai build, last 7 days. A version is compared once it has ${d?.min_episodes ?? 100} decided episodes.`}>
      {alert && (
        <Alert severity="error" style={{ marginBottom: 8 }}>
          {alert.version} is worse than {alert.previous}: goal rate {fmtRate(alert.goal_rate?.[0])} → {fmtRate(alert.goal_rate?.[1])}, bad claims {fmtRate(alert.bad_claim_rate?.[0])} → {fmtRate(alert.bad_claim_rate?.[1])}.
        </Alert>
      )}
      <AsyncSection loading={s.loading} notFound={s.notFound} error={s.error} empty={!(d?.versions || []).length} emptyTitle="No versioned verdicts yet" emptyDescription="Verdicts carry the build once the stamped gen-ai release is live.">
        <Grid
          columns={["Version", "First seen", "Episodes", "Goal rate", "Bad claims", "Missed handoff"]}
          numeric={[2, 5]}
          rows={[...(d?.versions || [])].reverse().map((v) => [
            <span style={{ fontFamily: MONO }}>{v.version}</span>, fmtTime(v.first_seen), v.episodes, fmtRate(v.goal_rate), fmtRate(v.bad_claim_rate), v.handoff_missed,
          ])}
        />
      </AsyncSection>
    </Section>
  );
}

function ToolFailures({ refreshKey }) {
  const classes = useStyles();
  const [date, setDate] = useState(() => istToday());
  const [s] = useLoad(() => toolFailures({ date }), [refreshKey, date]);
  const d = s.data;
  const tools = d?.tools || [];
  return (
    <Section
      title="Tool failures"
      subtitle="Tool calls and real failures in each night's judged episodes. Order quotes and missing options or addresses are conversation steps, not failures."
      action={<TextField type="date" size="small" variant="outlined" value={date} onChange={(e) => setDate(e.target.value)} />}
    >
      {d && <Typography component="div" className={classes.hint} style={{ marginBottom: 6 }}>{d.agents} agents{d.unreadable ? ` · ${d.unreadable} unreadable` : ""}</Typography>}
      <AsyncSection loading={s.loading} notFound={s.notFound} error={s.error} empty={!tools.length} emptyTitle="No tool calls recorded for this night">
        <Grid
          columns={["Tool", "Calls", "Failed", "Fail rate", "Reasons", "Agents failing"]}
          numeric={[1, 2]}
          rows={tools.map((t) => [
            <span style={{ fontFamily: MONO }}>{t.tool}</span>, t.calls, t.failed,
            <Badge tone={t.fail_rate > 0.2 ? "error" : t.fail_rate > 0.05 ? "warning" : "neutral"} subtle>{fmtRate(t.fail_rate)}</Badge>,
            Object.entries(t.types || {}).map(([k, v]) => `${k} ${v}`).join(" · ") || "—",
            <div title={(t.agents || []).map((a) => `${a.project_id} ${a.failed}/${a.calls}`).join("\n")}>{t.agents_failing}</div>,
          ])}
        />
      </AsyncSection>
    </Section>
  );
}

function Backlog({ refreshKey }) {
  const classes = useStyles();
  const [s] = useLoad(() => actionBacklog({ days: 7 }), [refreshKey]);
  const types = s.data?.types || [];
  return (
    <Section title="Action backlog" subtitle="What customers asked agents to do that they could not, last 7 days. A tool or handoff closes these, not knowledge.">
      <AsyncSection loading={s.loading} notFound={s.notFound} error={s.error} empty={!types.length} emptyTitle="No action gaps in the last 7 days">
        <Grid
          columns={["Request", "Agents", "Questions", "Top labels"]}
          numeric={[1, 2]}
          rows={types.map((t) => [
            t.action, t.agents, t.questions,
            (t.top || []).map((x, i) => (
              <div key={i}>
                {x.label} <span className={classes.hint}>· {x.count} · {x.project_id}</span>
              </div>
            )),
          ])}
        />
      </AsyncSection>
    </Section>
  );
}

export default function LoopTab({ refreshKey }) {
  return (
    <Box display="flex" flexDirection="column" gridGap={14}>
      <Readiness refreshKey={refreshKey} />
      <Promotions refreshKey={refreshKey} />
      <Agreement refreshKey={refreshKey} />
      <Versions refreshKey={refreshKey} />
      <ToolFailures refreshKey={refreshKey} />
      <Backlog refreshKey={refreshKey} />
    </Box>
  );
}
